import React, { useMemo, useState } from 'react';
import { ArrowUpCircle, ArrowDownCircle, History, X, Search, Filter, Pill } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from '../ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { formatDateTime } from '../../lib/utils';

export function MedicationMovementsSheet({
  movements = [],
  medications = [],
}) {
  const [open, setOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [medicationFilter, setMedicationFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Lookup categorías únicas de los medicamentos existentes
  const categories = useMemo(() => {
    const set = new Set();
    medications.forEach(m => { if (m.category) set.add(m.category); });
    return Array.from(set).sort();
  }, [medications]);

  const filtered = useMemo(() => {
    return movements.filter(m => {
      if (typeFilter !== 'all' && m.type !== typeFilter) return false;
      if (medicationFilter !== 'all' && m.medication_id !== medicationFilter) return false;
      if (categoryFilter !== 'all') {
        const med = medications.find(x => x.id === m.medication_id);
        if (!med || med.category !== categoryFilter) return false;
      }
      if (searchText) {
        const q = searchText.toLowerCase();
        const inName = (m.medication_name || '').toLowerCase().includes(q);
        const inNotes = (m.notes || '').toLowerCase().includes(q);
        const inSupplier = (m.supplier_name || '').toLowerCase().includes(q);
        const inRef = (m.reference || '').toLowerCase().includes(q);
        if (!inName && !inNotes && !inSupplier && !inRef) return false;
      }
      const movDate = m.created_at ? m.created_at.split('T')[0] : '';
      if (dateFrom && movDate < dateFrom) return false;
      if (dateTo && movDate > dateTo) return false;
      return true;
    });
  }, [movements, typeFilter, medicationFilter, categoryFilter, searchText, dateFrom, dateTo, medications]);

  const stats = useMemo(() => ({
    total: filtered.length,
    entradas: filtered.filter(m => m.type === 'entrada').length,
    salidas: filtered.filter(m => m.type === 'salida').length,
  }), [filtered]);

  const resetFilters = () => {
    setTypeFilter('all');
    setMedicationFilter('all');
    setCategoryFilter('all');
    setSearchText('');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = typeFilter !== 'all' || medicationFilter !== 'all' || categoryFilter !== 'all' || searchText || dateFrom || dateTo;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          className="border-2 border-[#931A25]/20 hover:border-[#931A25]/40 hover:bg-[#931A25]/5 text-[#931A25] font-medium gap-2"
          data-testid="open-medication-movements-sheet-btn"
        >
          <History className="h-4 w-4" />
          Movimientos Recientes
          {movements.length > 0 && (
            <Badge variant="secondary" className="ml-1 bg-[#931A25]/10 text-[#931A25] hover:bg-[#931A25]/20">
              {movements.length}
            </Badge>
          )}
        </Button>
      </SheetTrigger>

      <SheetContent side="right" className="w-full sm:max-w-5xl overflow-y-auto p-0" data-testid="medication-movements-sheet-content">
        {open && (
        <>
        {/* Header */}
        <div className="bg-gradient-to-r from-[#931A25] to-[#B32234] text-white p-6 sticky top-0 z-20 shadow-md">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2 text-2xl">
              <History className="h-6 w-6" />
              Historial de Medicamentos
            </SheetTitle>
            <SheetDescription className="text-white/80 mt-1">
              Entradas y salidas del inventario veterinario con filtros avanzados
            </SheetDescription>
          </SheetHeader>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="bg-white/10 backdrop-blur rounded-lg p-3 border border-white/20">
              <p className="text-xs text-white/70">Total mostrado</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-3 border border-white/20">
              <p className="text-xs text-white/70 flex items-center gap-1"><ArrowUpCircle className="h-3 w-3" /> Entradas</p>
              <p className="text-2xl font-bold text-green-200">{stats.entradas}</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-3 border border-white/20">
              <p className="text-xs text-white/70 flex items-center gap-1"><ArrowDownCircle className="h-3 w-3" /> Salidas</p>
              <p className="text-2xl font-bold text-red-200">{stats.salidas}</p>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="p-4 bg-slate-50 border-b border-slate-200 sticky top-[188px] z-10">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-slate-600" />
            <span className="text-sm font-semibold text-slate-700">Filtros</span>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={resetFilters} className="ml-auto h-7 text-xs text-slate-500 hover:text-[#931A25]">
                <X className="h-3 w-3 mr-1" /> Limpiar
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {/* Buscador */}
            <div className="lg:col-span-2 relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar medicamento, nota, proveedor o ref..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="pl-8 bg-white"
                data-testid="med-movements-search-input"
              />
            </div>

            {/* Tipo */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="bg-white" data-testid="med-movements-type-filter">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                <SelectItem value="entrada">
                  <span className="flex items-center gap-2"><ArrowUpCircle className="h-3 w-3 text-green-600" /> Entradas</span>
                </SelectItem>
                <SelectItem value="salida">
                  <span className="flex items-center gap-2"><ArrowDownCircle className="h-3 w-3 text-red-600" /> Salidas</span>
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Categoría */}
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="bg-white" data-testid="med-movements-category-filter">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categories.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Segunda fila: medicamento y fechas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
            <Select value={medicationFilter} onValueChange={setMedicationFilter}>
              <SelectTrigger className="bg-white" data-testid="med-movements-medication-filter">
                <SelectValue placeholder="Medicamento" />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">Todos los medicamentos</SelectItem>
                {medications.map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-white h-9"
                data-testid="med-movements-date-from"
                placeholder="Desde"
              />
            </div>
            <div>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-white h-9"
                data-testid="med-movements-date-to"
                placeholder="Hasta"
              />
            </div>
          </div>
        </div>

        {/* Tabla */}
        <div className="p-4">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Pill className="h-12 w-12 mb-3" />
              <p className="font-medium">No hay movimientos que coincidan con los filtros</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={resetFilters} className="mt-2 text-[#931A25]">
                  Limpiar filtros
                </Button>
              )}
            </div>
          ) : (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="w-[140px]">Fecha</TableHead>
                    <TableHead>Medicamento</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead className="text-center w-[110px]">Tipo</TableHead>
                    <TableHead className="text-right">Cantidad</TableHead>
                    <TableHead>Notas / Ref</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(movement => {
                    const med = medications.find(m => m.id === movement.medication_id);
                    return (
                      <TableRow key={movement.id} className={`hover:bg-slate-50 ${movement.type === 'salida' ? 'bg-red-50/30' : ''}`}>
                        <TableCell className="text-sm whitespace-nowrap text-slate-600">
                          {formatDateTime(movement.created_at)}
                        </TableCell>
                        <TableCell className="font-medium">{movement.medication_name}</TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {med?.category || '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {movement.type === 'entrada' ? (
                            <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-green-200">
                              <ArrowUpCircle className="h-3 w-3 mr-1" /> Entrada
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 hover:bg-red-100 border-red-200">
                              <ArrowDownCircle className="h-3 w-3 mr-1" /> Salida
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {movement.quantity} <span className="text-xs text-slate-400">{med?.presentation || med?.unit || ''}</span>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-xs">
                          <div className="truncate" title={movement.notes || movement.reference || ''}>
                            {movement.notes || movement.reference || '-'}
                          </div>
                          {movement.supplier_name && (
                            <div className="text-[10px] text-slate-400 mt-0.5">Prov: {movement.supplier_name}</div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
        </>
        )}
      </SheetContent>
    </Sheet>
  );
}
